import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/auth-context';
import axios from 'axios';
import { toast } from 'react-toastify';
import { rootUrl, formatToZMW } from '../../helpers';
import { 
  FaUsers, 
  FaMoneyBillWave, 
  FaReceipt, 
  FaFileInvoiceDollar, 
  FaProjectDiagram, 
  FaBook, 
  FaChartLine,
  FaSpinner,
  FaUserCheck,
  FaUserClock,
  FaCalendarAlt
} from 'react-icons/fa';
import { MdDashboard } from 'react-icons/md';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useAuth();

  const fetchStats = async () => {
    try {
      const response = await axios.get(rootUrl(`/getAdminStats/${auth.user.cid}`));
      if (response.data.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.cid) {
      fetchStats();
    }
  }, [auth.user?.cid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="spinner text-blue-600 text-4xl" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No statistics available</p>
      </div>
    );
  }

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <div className={`bg-white p-6 rounded-lg shadow-md border-l-4 ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('border-l-', 'bg-').replace('-500', '-100')}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className='bg-white p-6 rounded-lg shadow'>
      <div className='flex items-center justify-between font-semi-bold text-lg mb-6'>
        <span className='flex items-center'>
          <MdDashboard className="mr-2" />
          Admin Dashboard
        </span>
      </div>
      <hr className="mb-6" />

      {/* User Statistics */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">User Management</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Users"
            value={stats.users.total}
            icon={<FaUsers className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
          />
          <StatCard
            title="Active Users"
            value={stats.users.active}
            icon={<FaUserCheck className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
            subtitle={`${((stats.users.active / stats.users.total) * 100).toFixed(1)}% of total`}
          />
          <StatCard
            title="Pending Users"
            value={stats.users.pending}
            icon={<FaUserClock className="text-yellow-600 text-xl" />}
            color="border-l-yellow-500"
            subtitle="Awaiting approval"
          />
        </div>
      </div>

      {/* Financial Statistics */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Total Income"
            value={formatToZMW(stats.transactions.income)}
            icon={<FaMoneyBillWave className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
          />
          <StatCard
            title="Total Expenses"
            value={formatToZMW(stats.transactions.expenses)}
            icon={<FaMoneyBillWave className="text-red-600 text-xl" />}
            color="border-l-red-500"
          />
          <StatCard
            title="Net Balance"
            value={formatToZMW(stats.transactions.net)}
            icon={<FaChartLine className="text-blue-600 text-xl" />}
            color={`border-l-${stats.transactions.net >= 0 ? 'green' : 'red'}-500`}
            subtitle={stats.transactions.net >= 0 ? 'Positive balance' : 'Negative balance'}
          />
          <StatCard
            title="Total Transactions"
            value={stats.transactions.total}
            icon={<FaBook className="text-purple-600 text-xl" />}
            color="border-l-purple-500"
          />
        </div>
      </div>

      {/* System Statistics */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">System Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Banks"
            value={stats.banks.total}
            icon={<FaUsers className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
          />
          <StatCard
            title="Budget Items"
            value={stats.budget.items}
            icon={<FaMoneyBillWave className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
            subtitle={`Total: ${formatToZMW(stats.budget.amount)}`}
          />
          <StatCard
            title="Projects"
            value={stats.projects.total}
            icon={<FaProjectDiagram className="text-purple-600 text-xl" />}
            color="border-l-purple-500"
          />
        </div>
      </div>

      {/* Business Statistics */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Suppliers"
            value={stats.suppliers.total}
            icon={<FaUsers className="text-orange-600 text-xl" />}
            color="border-l-orange-500"
          />
          <StatCard
            title="Customers"
            value={stats.customers.total}
            icon={<FaUsers className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
          />
          <StatCard
            title="Receipts"
            value={stats.receipts.total}
            icon={<FaReceipt className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
            subtitle={`Total: ${formatToZMW(stats.receipts.amount)}`}
          />
          <StatCard
            title="Bills"
            value={stats.bills.total}
            icon={<FaFileInvoiceDollar className="text-red-600 text-xl" />}
            color="border-l-red-500"
            subtitle={`Total: ${formatToZMW(stats.bills.amount)}`}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity (Last 7 Days)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Recent Transactions"
            value={stats.recent.transactions}
            icon={<FaBook className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
            subtitle="Last 7 days"
          />
          <StatCard
            title="Recent Receipts"
            value={stats.recent.receipts}
            icon={<FaReceipt className="text-blue-600 text-xl" />}
            color="border-l-blue-500"
            subtitle="Last 7 days"
          />
        </div>
      </div>

      {/* Additional Statistics */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Journal Entries"
            value={stats.journals.total}
            icon={<FaBook className="text-purple-600 text-xl" />}
            color="border-l-purple-500"
          />
          {/* <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Quick Actions</h4>
            <div className="space-y-2">
              <button className="w-full text-left text-sm text-blue-600 hover:text-blue-800">
                • View pending user approvals
              </button>
              <button className="w-full text-left text-sm text-blue-600 hover:text-blue-800">
                • Review recent transactions
              </button>
              <button className="w-full text-left text-sm text-blue-600 hover:text-blue-800">
                • Check system health
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}
